import {
  isHashtagTerm,
  SOCIAL_DEFAULT_CADENCE,
  SOCIAL_DEFAULT_DEPTH_DAYS,
  SOCIAL_DEFAULT_FIRST_COLLECTION_POST_LIMIT,
  SOCIAL_DEFAULT_RUN_POST_LIMIT,
  toHashtag,
} from '@digital-twins/shared-types'
import type {
  SocialTrackerItem,
  SocialTrackerProposal,
  SocialTrackerInput,
  SocialTrackerUpdate,
  SocialTrackerLabel,
  SocialTrackerLabelKind,
} from '@digital-twins/shared-types'
import type {
  TrackerFormState,
  RequiredField,
} from '@/types/components/social-tracker-dialog'

export const MAX_TRACKER_TERMS = 20
export const MAX_TRACKER_TERM_LENGTH = 100
export const MAX_TRACKER_LABELS = 10

/** A typed `#tag` becomes the hashtag every channel accepts; other terms stay as typed. */
function cleanSocialTerm(value: string): string {
  const trimmed = value.trim()
  if (!isHashtagTerm(trimmed)) {
    return trimmed
  }
  const tag = toHashtag(trimmed)
  return tag ? `#${tag}` : ''
}

/**
 * Literal comma-separated input only; this does not interpret topic meaning.
 * `#` terms are cleaned to a valid hashtag (dropped when nothing remains), and
 * duplicates are dropped case-insensitively, keeping the first spelling.
 */
export function addSocialTerms(current: string[], draft: string): string[] {
  const seen = new Set<string>()
  const next: Array<string> = []
  const candidates = [...current, ...draft.split(',').map(cleanSocialTerm)]
  for (const value of candidates) {
    const key = value.toLowerCase()
    if (!value || seen.has(key)) {
      continue
    }
    seen.add(key)
    next.push(value)
  }
  return next
}

export function initialFormState(
  tracker: SocialTrackerItem | null,
  proposal: SocialTrackerProposal | null,
): TrackerFormState {
  const source = tracker ?? proposal
  return {
    name: source?.name ?? '',
    platform: source?.platform ?? 'reddit',
    keywords: source?.keywords ?? [],
    handles: source?.handles ?? [],
    language: source?.language ?? 'en',
    market: source?.market ?? '',
    // A proposal names a market but carries no code: picking a list entry sets it.
    marketCode: tracker?.marketCode ?? null,
    labels: source?.labels ?? [],
    brief: source?.brief ?? { include: '', exclude: '' },
    keywordNotes: source?.keywordNotes ?? [],
    topics: source?.topics ?? [],
    depthDays: tracker?.depthDays ?? SOCIAL_DEFAULT_DEPTH_DAYS,
    cadence: tracker?.cadence ?? SOCIAL_DEFAULT_CADENCE,
    runPostLimit: tracker?.runPostLimit ?? SOCIAL_DEFAULT_RUN_POST_LIMIT,
    firstCollectionPostLimit:
      tracker?.firstCollectionPostLimit ?? SOCIAL_DEFAULT_FIRST_COLLECTION_POST_LIMIT,
  }
}

/** Range, cadence and post limit as the API takes them, on create and on edit. */
export function scheduleFields(form: TrackerFormState) {
  return {
    depthDays: form.depthDays,
    cadence: form.cadence,
    runPostLimit: form.runPostLimit,
    firstCollectionPostLimit: form.firstCollectionPostLimit,
  }
}

/** The brief as the API takes it: notes of current keywords, named topics only. */
export function relevanceFields(form: TrackerFormState) {
  return {
    brief: form.brief,
    keywordNotes: form.keywordNotes.filter(
      (note) =>
        form.keywords.includes(note.keyword) && note.description.trim() !== '',
    ),
    topics: form.topics.filter((topic) => topic.name.trim() !== ''),
  }
}

export function labelValues(
  labels: Array<SocialTrackerLabel>,
  kind: SocialTrackerLabelKind,
) {
  return labels
    .filter((label) => label.kind === kind)
    .map((label) => label.value)
}

export function withLabelValues(
  labels: Array<SocialTrackerLabel>,
  kind: SocialTrackerLabelKind,
  values: Array<string>,
): Array<SocialTrackerLabel> {
  return [
    ...labels.filter((label) => label.kind !== kind),
    ...values.map((value) => ({ kind, value })),
  ]
}

/** The selected market owns the one market tag written to Library documents. */
export function trackerLabels(
  form: TrackerFormState,
): Array<SocialTrackerLabel> {
  return withLabelValues(
    form.labels,
    'market',
    form.market.trim() ? [form.market.trim()] : [],
  )
}

export function missingRequiredFields(
  form: TrackerFormState,
): Array<RequiredField> {
  const missing: Array<RequiredField> = []
  if (!form.name.trim()) {
    missing.push('name')
  }
  if (form.keywords.length === 0) {
    missing.push('keywords')
  }
  if (!form.language.trim()) {
    missing.push('language')
  }
  if (!form.market.trim()) {
    missing.push('market')
  }
  return missing
}

export function toTrackerInput(form: TrackerFormState): SocialTrackerInput {
  return {
    name: form.name.trim(),
    platform: form.platform,
    keywords: form.keywords,
    ...(form.handles.length > 0 ? { handles: form.handles } : {}),
    language: form.language.trim(),
    market: form.market.trim(),
    marketCode: form.marketCode,
    labels: trackerLabels(form),
    ...relevanceFields(form),
    ...scheduleFields(form),
  }
}

export function toTrackerUpdate(form: TrackerFormState): SocialTrackerUpdate {
  return {
    name: form.name.trim(),
    keywords: form.keywords,
    handles: form.handles,
    language: form.language.trim(),
    market: form.market.trim(),
    labels: trackerLabels(form),
    ...relevanceFields(form),
    ...scheduleFields(form),
  }
}
